/**
 * Store Zustand pour la gestion des factures
 * Remplace useInvoices, useCreateInvoice, useUpdateInvoice, useDeleteInvoice
 * Architecture 3-couches conforme au CONTEXT.md
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { invoicesAPI } from '../utils/api';
import type { Invoice, InvoiceStatus } from '../types/models';

interface InvoicesState {
  // État des données
  invoices: Invoice[];
  currentInvoice: Invoice | null;
  
  // État de chargement
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  
  // État d'erreur
  error: string | null;
  
  // Actions de lecture
  fetchInvoices: (params?: {
    search?: string;
    sort?: string;
    order?: 'asc' | 'desc';
    status?: string;
  }) => Promise<void>;
  
  fetchInvoice: (id: string) => Promise<void>;
  
  // Actions d'écriture
  createInvoice: (invoiceData: any) => Promise<Invoice>;
  updateInvoice: (id: string, invoiceData: any) => Promise<Invoice>;
  deleteInvoice: (id: string) => Promise<void>;
  
  // Actions spéciales
  markAsPaid: (id: string, paidAmount: number) => Promise<void>;
  updateStatus: (id: string, status: string) => Promise<void>;
  
  // Actions utilitaires
  clearError: () => void;
  clearCurrentInvoice: () => void;
}

export const useInvoicesStore = create<InvoicesState>()(
  devtools(
    (set, get) => ({
      // État initial
      invoices: [],
      currentInvoice: null,
      isLoading: false,
      isCreating: false,
      isUpdating: false,
      isDeleting: false,
      error: null,

      // Actions de lecture
      fetchInvoices: async (_params) => {
        set({ isLoading: true, error: null });
        try {
          // Note: invoicesAPI.getAll doesn't accept params
          const data = await invoicesAPI.getAll();
          const invoices = Array.isArray(data) ? data : (data?.invoices || data || []);
          set({
            invoices,
            isLoading: false
          });
        } catch (error: any) {
          set({
            error: error.message || 'Erreur lors de la récupération des factures',
            isLoading: false
          });
        }
      },

      fetchInvoice: async (id) => {
        set({ isLoading: true, error: null });
        try {
          const invoice = await invoicesAPI.getById(id);
          set({ 
            currentInvoice: invoice, 
            isLoading: false 
          });
        } catch (error: any) {
          set({ 
            error: error.message || 'Erreur lors de la récupération de la facture',
            isLoading: false 
          });
        }
      },

      // Actions d'écriture
      createInvoice: async (invoiceData) => {
        set({ isCreating: true, error: null });
        try {
          const newInvoice = await invoicesAPI.create(invoiceData);
          set(state => ({
            invoices: [newInvoice, ...state.invoices],
            isCreating: false
          }));
          return newInvoice;
        } catch (error: any) {
          set({ 
            error: error.message || 'Erreur lors de la création de la facture',
            isCreating: false 
          });
          throw error;
        }
      },

      updateInvoice: async (id, invoiceData) => {
        set({ isUpdating: true, error: null });
        try {
          const updatedInvoice = await invoicesAPI.update(id, invoiceData);
          set(state => ({
            invoices: state.invoices.map(i => i.id === id ? updatedInvoice : i),
            currentInvoice: state.currentInvoice?.id === id ? updatedInvoice : state.currentInvoice,
            isUpdating: false
          }));
          return updatedInvoice;
        } catch (error: any) {
          set({ 
            error: error.message || 'Erreur lors de la mise à jour de la facture',
            isUpdating: false 
          });
          throw error;
        }
      },

      deleteInvoice: async (id) => {
        set({ isDeleting: true, error: null });
        try {
          await invoicesAPI.delete(id);
          set(state => ({
            invoices: state.invoices.filter(i => i.id !== id),
            currentInvoice: state.currentInvoice?.id === id ? null : state.currentInvoice,
            isDeleting: false
          }));
        } catch (error: any) {
          set({ 
            error: error.message || 'Erreur lors de la suppression de la facture',
            isDeleting: false 
          });
          throw error;
        }
      },

      // Actions spéciales
      markAsPaid: async (id, paidAmount) => {
        set({ isUpdating: true, error: null });
        try {
          await invoicesAPI.markAsPaid(id, paidAmount);
          set(state => ({
            invoices: state.invoices.map(i =>
              i.id === id ? { ...i, status: 'PAID' as const } : i
            ),
            currentInvoice: state.currentInvoice?.id === id
              ? { ...state.currentInvoice, status: 'PAID' as const }
              : state.currentInvoice,
            isUpdating: false
          }));
        } catch (error: any) {
          set({ 
            error: error.message || 'Erreur lors du marquage comme payé',
            isUpdating: false 
          });
          throw error;
        }
      },

      updateStatus: async (id, status) => {
        set({ isUpdating: true, error: null });
        try {
          await invoicesAPI.updateStatus(id, status);
          set(state => ({
            invoices: state.invoices.map(i =>
              i.id === id ? { ...i, status: status as InvoiceStatus } : i
            ),
            currentInvoice: state.currentInvoice?.id === id
              ? { ...state.currentInvoice, status: status as InvoiceStatus }
              : state.currentInvoice,
            isUpdating: false
          }));
        } catch (error: any) {
          set({ 
            error: error.message || 'Erreur lors du changement de statut',
            isUpdating: false 
          });
          throw error;
        }
      },

      // Actions utilitaires
      clearError: () => set({ error: null }),
      clearCurrentInvoice: () => set({ currentInvoice: null }),
    }),
    {
      name: 'invoices-store',
    }
  )
);
